import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ExtractedData, UploadResponse } from "../api";
import { pdfUrl } from "../api";
import { PdfViewer, type Overlay } from "./PdfViewer";
import { ValidationPanel } from "./ValidationPanel";

type Props = {
  result: UploadResponse;
  filename?: string;
  onRetryOcr?: () => void;
};

/**
 * Visueel-eerst view: links PDF met overlays, rechts de velden-/tabellen-lijst.
 * Hover-sync in beide richtingen zodat je snel ziet of de match klopt.
 */
export function InputOutputView({ result, filename, onRetryOcr }: Props) {
  const kb = Math.round(result.bytes / 1024);
  const pages = result.turboocr.paginas;
  const pagesLabel = pages === 1 ? "1 pagina" : `${pages} pagina's`;

  const [activeId, setActiveId] = useState<string | null>(null);

  const overlays = useMemo<Overlay[]>(() => buildOverlays(result.extracted), [result.extracted]);
  const outputData = buildOutput(result);

  const hasPdf = !!result.sha256;

  return (
    <div className="space-y-4">
      {/* HEADER CARD — compact */}
      <section className="bg-white border rounded-lg">
        <div className="px-5 py-3 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">Document binnen</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {filename || "upload.pdf"} · {kb} KB · {pagesLabel} · OCR {result.turboocr_latency_ms}ms
            </p>
          </div>
          <div className="text-right text-sm">
            {result.verdict === "match" && result.matched_type_naam ? (
              <>
                <div className="font-medium text-green-700">✓ {result.matched_type_naam}</div>
                <div className="text-xs font-mono text-gray-500">
                  zekerheid {(result.matches[0]?.score ?? 0).toFixed(3)}
                </div>
              </>
            ) : result.verdict === "twijfel" ? (
              <div className="font-medium text-amber-700">? mogelijk {result.matches[0]?.naam ?? "onbekend"}</div>
            ) : (
              <div className="font-medium text-blue-700">nieuw document-type</div>
            )}
          </div>
        </div>
      </section>

      {/* Vierkants-controle resultaten: boven de preview zodat je direct ziet
          of de extractie mathematisch consistent is. */}
      {result.extracted?.validaties && result.extracted.validaties.length > 0 && (
        <ValidationPanel validaties={result.extracted.validaties} />
      )}

      {/* SPLIT PANE — toont zodra extractie is uitgevoerd (match óf twijfel).
          Bij twijfel staat de keuze-banner er al boven, geplaatst door VerdictPanel. */}
      {result.extracted && hasPdf ? (
        <SplitView
          pdfUrl={pdfUrl(result.sha256)}
          result={result}
          overlays={overlays}
          activeId={activeId}
          setActiveId={setActiveId}
          outputData={outputData}
          onRetryOcr={onRetryOcr}
        />
      ) : null}

      {/* Technische details — klein, onderaan */}
      <details className="bg-white border rounded">
        <summary className="cursor-pointer px-4 py-2 text-xs text-gray-500 hover:bg-gray-50">
          Technische details (fingerprint, scores, OCR-layout)
        </summary>
        <div className="px-4 py-3 border-t text-xs space-y-3">
          <div>
            <strong>Match-breakdown:</strong>
            <table className="w-full mt-1">
              <thead>
                <tr className="text-left text-gray-500">
                  <th>Type</th>
                  <th>Score</th>
                  <th>pHash</th>
                  <th>Structuur</th>
                  <th>Anchors</th>
                </tr>
              </thead>
              <tbody>
                {result.matches.map((m) => (
                  <tr key={m.type_id} className="font-mono">
                    <td>{m.naam}</td>
                    <td>{m.score.toFixed(3)}</td>
                    <td>{m.components.phash.toFixed(2)}</td>
                    <td>{m.components.structuur.toFixed(2)}</td>
                    <td>{m.components.anchors.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <strong>Fingerprint:</strong>
            <div className="font-mono mt-1">phash: {result.fingerprint.phash}</div>
            <div className="font-mono">struct: {result.fingerprint.structuur_hash}</div>
          </div>
          <div>
            <strong>TurboOCR per pagina:</strong>
            <pre className="bg-gray-50 p-2 rounded overflow-auto text-xs">
              {JSON.stringify(result.turboocr.per_pagina, null, 2)}
            </pre>
          </div>
        </div>
      </details>
    </div>
  );
}

function SplitView({
  pdfUrl,
  result,
  overlays,
  activeId,
  setActiveId,
  outputData,
  onRetryOcr,
}: {
  pdfUrl: string;
  result: UploadResponse;
  overlays: Overlay[];
  activeId: string | null;
  setActiveId: (id: string | null) => void;
  outputData: Record<string, unknown>;
  onRetryOcr?: () => void;
}) {
  const extracted = result.extracted!;
  // tabellen-telling blijft identiek; velden worden pas in FieldsSidebar
  // uitgevouwen (dot-paths + dedupe platte aliassen).
  const veldenEntries = dedupeAliases(
    flattenAny((extracted.velden ?? {}) as Record<string, unknown>),
  );
  const tabellenEntries = Object.entries(extracted.tabellen);
  const totalRows = tabellenEntries.reduce((n, [, rows]) => n + rows.length, 0);
  const [showJson, setShowJson] = useState(false);
  const [copied, setCopied] = useState(false);
  const sidebarRef = useRef<HTMLElement | null>(null);

  // Resizable splitter: pointer-driven width-slider tussen PDF (links) en
  // velden-paneel (rechts). Breedte wordt bewaard in localStorage zodat de
  // voorkeur per browser blijft, bootstrappe-waarde = 460 (oude vaste maat).
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    if (typeof window === "undefined") return 460;
    const saved = window.localStorage.getItem("forms-sidebar-width");
    const n = saved ? parseInt(saved, 10) : NaN;
    return Number.isFinite(n) && n >= 300 && n <= 900 ? n : 460;
  });
  const widthRef = useRef(sidebarWidth);
  useEffect(() => { widthRef.current = sidebarWidth; }, [sidebarWidth]);

  const startResize = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = widthRef.current;
    const onMove = (ev: PointerEvent) => {
      // Divider zit tussen PDF (links) en sidebar (rechts); muis naar LINKS
      // → sidebar wordt BREDER. Dus delta aftrekken van startW.
      const delta = ev.clientX - startX;
      const next = Math.max(300, Math.min(900, startW - delta));
      setSidebarWidth(next);
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      try {
        window.localStorage.setItem("forms-sidebar-width", String(widthRef.current));
      } catch { /* localStorage disabled → geen crash */ }
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    // Visuele feedback tijdens drag: cursor + geen text-selection
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  const copyJson = async () => {
    await navigator.clipboard.writeText(JSON.stringify(outputData, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Overlay-klik: scroll de rechter-paneel-entry naar dezelfde viewport-hoogte als
  // de bbox op de PDF. "Naast je" dus — klik een tabel, zie dezelfde tabel rechts
  // op dezelfde hoogte. Gebruik getBoundingClientRect ipv offsetTop om safe te zijn
  // voor positioning-context (offsetParent kan buiten de sidebar liggen).
  const handleOverlayClick = useCallback((id: string, viewportTop: number) => {
    setActiveId(id);
    const sidebar = sidebarRef.current;
    if (!sidebar) return;
    const entry = sidebar.querySelector(`[data-sidebar-id="${CSS.escape(id)}"]`) as HTMLElement | null;
    if (!entry) return;
    const entryTop = entry.getBoundingClientRect().top;
    const delta = entryTop - viewportTop;
    sidebar.scrollTo({ top: sidebar.scrollTop + delta, behavior: "smooth" });
  }, [setActiveId]);

  return (
    <section className="bg-white border rounded-lg overflow-hidden">
      <header className="bg-emerald-50 px-5 py-3 border-b border-emerald-100 flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-emerald-900">Herkende velden</h2>
          <div className="flex items-center gap-3 text-xs text-emerald-800 mt-0.5">
            <LegendDot kind="veld" label={`${veldenEntries.length} velden`} />
            <LegendDot kind="tabel" label={`${tabellenEntries.length} tabellen · ${totalRows} rijen`} />
            {extracted.mist.length > 0 && (
              <LegendDot kind="mist" label={`${extracted.mist.length} gemist`} />
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* "Opnieuw met OCR" verschijnt alleen als pipeline onzeker is:
              ≥1 missend veld (regex/alias/fuzzy vond het niet) → mogelijk helpt
              TurboOCR's layout-detectie bij slecht uitgelijnde tabellen /
              scanned-pagina-bijlages die pdfplumber oversloeg. */}
          {onRetryOcr && extracted.mist.length > 0 && (
            <button
              onClick={onRetryOcr}
              className="text-xs bg-amber-600 text-white px-3 py-1 rounded hover:bg-amber-700"
              title="Draai de extractie opnieuw met TurboOCR i.p.v. pdfplumber. Trager maar robuuster voor scanned pagina's of complexe tabellen."
            >
              ↻ Opnieuw met OCR
            </button>
          )}
          <button
            onClick={() => setShowJson((s) => !s)}
            className="text-xs text-emerald-700 hover:text-emerald-900 underline"
          >
            {showJson ? "verberg JSON" : "toon JSON"}
          </button>
          <button
            onClick={copyJson}
            className="text-xs bg-emerald-600 text-white px-3 py-1 rounded hover:bg-emerald-700"
          >
            {copied ? "✓ gekopieerd" : "Kopieer JSON"}
          </button>
          <a
            href={`/api/upload/${result.sha256}/export.xlsx`}
            download
            className="text-xs bg-emerald-700 text-white px-3 py-1 rounded hover:bg-emerald-800 inline-flex items-center gap-1"
            title="Download alle velden en tabellen als Excel-bestand"
          >
            ⬇ Excel
          </a>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row h-[calc(100vh-220px)] min-h-[600px]">
        <div className="flex-1 min-w-0 h-full overflow-hidden">
          <PdfViewer
            pdfUrl={pdfUrl}
            pageDims={extracted.page_dims}
            overlays={overlays}
            activeId={activeId}
            onHover={setActiveId}
            onOverlayClick={handleOverlayClick}
          />
        </div>

        {/* Draggable divider — alleen op lg+ zichtbaar (op mobile stackt
            de sidebar onder de PDF; horizontaal resizen heeft dan geen zin). */}
        <div
          onPointerDown={startResize}
          className="hidden lg:block w-1 flex-shrink-0 bg-gray-200 hover:bg-blue-400 active:bg-blue-500 cursor-col-resize transition-colors"
          role="separator"
          aria-orientation="vertical"
          aria-label="Breedte paneel aanpassen"
          title="Sleep om breedte aan te passen"
        />

        <aside
          ref={sidebarRef}
          style={{ width: `${sidebarWidth}px` }}
          className="w-full lg:w-auto flex-shrink-0 border-t lg:border-t-0 lg:border-l bg-gray-50 overflow-y-auto h-full min-h-0"
        >
          <FieldsSidebar
            extracted={extracted}
            activeId={activeId}
            setActiveId={setActiveId}
          />
        </aside>
      </div>

      {showJson && (
        <div className="border-t p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            API-response (zoals TTG/Import 'm krijgt)
          </h3>
          <pre className="bg-gray-900 text-emerald-200 p-3 rounded text-xs overflow-auto max-h-96">
            {JSON.stringify(outputData, null, 2)}
          </pre>
        </div>
      )}
    </section>
  );
}

function flattenAny(obj: Record<string, unknown>, prefix = ""): [string, string][] {
  const out: [string, string][] = [];
  for (const [k, v] of Object.entries(obj || {})) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === "object" && !Array.isArray(v)) {
      out.push(...flattenAny(v as Record<string, unknown>, key));
    } else {
      out.push([key, v === null || v === undefined ? "" : String(v)]);
    }
  }
  return out;
}

function dedupeAliases(rows: [string, string][]): [string, string][] {
  const nestedLeaves = new Map<string, Set<string>>();
  for (const [k, v] of rows) {
    if (k.includes(".")) {
      const leaf = k.split(".").pop()!;
      if (!nestedLeaves.has(leaf)) nestedLeaves.set(leaf, new Set());
      nestedLeaves.get(leaf)!.add(v);
    }
  }
  return rows.filter(([k, v]) => {
    if (k.includes(".")) return true;
    const vals = nestedLeaves.get(k);
    return !vals || !vals.has(v);
  });
}

/** Hit-lookup op dot-path: probeer eerst de volle nested-path, anders
 *  per segment door hits-tree navigeren, anders de leaf-key (platte alias).
 */
function lookupHit(path: string, hits: Record<string, unknown> | undefined): { bbox?: unknown; source?: string } | undefined {
  if (!hits) return undefined;
  // 1. direct platte key
  const direct = hits[path];
  if (direct && typeof direct === "object" && "source" in (direct as Record<string, unknown>)) {
    return direct as { bbox?: unknown; source?: string };
  }
  // 2. nested walk
  const parts = path.split(".");
  let cur: unknown = hits;
  for (const p of parts) {
    if (cur && typeof cur === "object") {
      cur = (cur as Record<string, unknown>)[p];
    } else {
      cur = undefined;
      break;
    }
  }
  if (cur && typeof cur === "object" && "source" in (cur as Record<string, unknown>)) {
    return cur as { bbox?: unknown; source?: string };
  }
  // 3. leaf fallback
  const leaf = parts[parts.length - 1];
  const leafHit = hits[leaf];
  if (leafHit && typeof leafHit === "object" && "source" in (leafHit as Record<string, unknown>)) {
    return leafHit as { bbox?: unknown; source?: string };
  }
  return undefined;
}

function FieldsSidebar({
  extracted,
  activeId,
  setActiveId,
}: {
  extracted: ExtractedData;
  activeId: string | null;
  setActiveId: (id: string | null) => void;
}) {
  const veldenEntries = dedupeAliases(
    flattenAny((extracted.velden ?? {}) as Record<string, unknown>),
  );
  const tabellenEntries = Object.entries(extracted.tabellen);

  return (
    <div className="p-4 space-y-5 text-sm">
      {veldenEntries.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Velden
          </h3>
          <div className="divide-y border rounded bg-white">
            {veldenEntries.map(([k, v]) => {
              const hit = lookupHit(k, extracted.velden_hits as Record<string, unknown> | undefined);
              const inTable = hit?.source === "table";
              const id = `veld:${k}`;
              // Overlay én hover-sync alleen bij losse bbox; in-table velden tonen we
              // zonder sync (we weten niet welke specifieke tabel, en overlay zou
              // tabel-outline overlappen).
              const hasOverlay = !!hit?.bbox && !inTable;
              const active = activeId === id && hasOverlay;
              return (
                <div
                  key={k}
                  data-sidebar-id={id}
                  onMouseEnter={() => hasOverlay && setActiveId(id)}
                  onMouseLeave={() => hasOverlay && setActiveId(null)}
                  className={`px-3 py-2 flex items-center justify-between transition-colors ${
                    hasOverlay ? "cursor-pointer" : ""
                  } ${active ? "bg-emerald-50" : ""}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-gray-500 font-mono truncate">{k}</div>
                    <div className="font-medium text-gray-900 truncate">{v}</div>
                    {inTable && (
                      <div className="text-[10px] text-blue-600 mt-0.5">uit tabel-cel</div>
                    )}
                  </div>
                  {hasOverlay && (
                    <span className="ml-2 w-2.5 h-2.5 rounded-sm bg-emerald-500 flex-shrink-0" title="eigen bbox" />
                  )}
                  {inTable && (
                    <span className="ml-2 w-2.5 h-2.5 rounded-sm bg-blue-500 flex-shrink-0" title="hoort bij tabel" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {extracted.mist.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-red-600 mb-2">
            Niet gevonden
          </h3>
          <div className="divide-y border rounded bg-white">
            {extracted.mist.map((v) => {
              const id = `mist:${v}`;
              const hint = extracted.mist_hints[v];
              const candidates = extracted.mist_candidates?.[v] || [];
              const hasBbox = !!hint?.bbox;
              const active = activeId === id;
              return (
                <div
                  key={v}
                  data-sidebar-id={id}
                  className={`px-3 py-2 transition-colors ${active ? "bg-red-50" : ""}`}
                >
                  <div
                    onMouseEnter={() => hasBbox && setActiveId(id)}
                    onMouseLeave={() => hasBbox && setActiveId(null)}
                    className={`flex items-center justify-between ${hasBbox ? "cursor-pointer" : ""}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-mono text-red-700 truncate">{v}</div>
                      {hint ? (
                        <div className="text-xs text-gray-500 truncate">
                          label "{hint.label}" gevonden, geen waarde
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">label niet in document</div>
                      )}
                    </div>
                    {hasBbox && <span className="ml-2 w-2.5 h-2.5 rounded-sm bg-red-500 flex-shrink-0" />}
                  </div>
                  {candidates.length > 0 && (
                    <details className="mt-1">
                      <summary className="text-[11px] text-amber-700 cursor-pointer hover:text-amber-900">
                        misschien bedoeld ({candidates.length})
                      </summary>
                      <ul className="mt-1 space-y-1">
                        {candidates.map((c, i) => (
                          <li
                            key={i}
                            className="text-[11px] bg-amber-50 border border-amber-200 rounded px-2 py-1"
                          >
                            <div className="font-mono font-medium text-amber-900">{c.value}</div>
                            <div className="text-gray-500 truncate" title={c.context}>
                              …{c.context}…
                            </div>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tabellenEntries.map(([naam, rows]) => {
        const id = `tabel:${naam}`;
        const meta = extracted.tabellen_meta[naam];
        const hasBbox = !!meta?.bbox;
        const active = activeId === id;
        return (
          <div key={naam} data-sidebar-id={id}>
            <div
              onMouseEnter={() => hasBbox && setActiveId(id)}
              onMouseLeave={() => hasBbox && setActiveId(null)}
              className={`flex items-center justify-between mb-2 ${hasBbox ? "cursor-pointer" : ""} ${
                active ? "bg-blue-50 -mx-1 px-1 rounded" : ""
              }`}
            >
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                <span className="font-mono">{naam}</span>{" "}
                <span className="text-gray-400 font-normal normal-case">({rows.length} rijen)</span>
              </h3>
              {hasBbox && <span className="w-2.5 h-2.5 rounded-sm bg-blue-500" />}
            </div>
            {rows.length === 0 ? (
              <div className="text-xs text-gray-500 bg-white border rounded px-2 py-1">
                Geen rijen gevonden.
              </div>
            ) : (
              <div className="overflow-x-auto border rounded bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(rows[0]).map((col) => (
                        <th key={col} className="border-b px-2 py-1 text-left font-medium font-mono">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 20).map((row, i) => (
                      <tr key={i} className="border-b last:border-0">
                        {Object.values(row).map((cell, j) => (
                          <td key={j} className="px-2 py-1">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 20 && (
                  <div className="text-xs text-gray-500 px-2 py-1 border-t">
                    … nog {rows.length - 20} rijen (zichtbaar in JSON)
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LegendDot({ kind, label }: { kind: "veld" | "tabel" | "mist"; label: string }) {
  const color = kind === "veld" ? "bg-emerald-500" : kind === "tabel" ? "bg-blue-500" : "bg-red-500";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2.5 h-2.5 rounded-sm ${color}`} />
      <span>{label}</span>
    </span>
  );
}

function buildOverlays(extracted: ExtractedData | null): Overlay[] {
  if (!extracted) return [];
  const out: Overlay[] = [];
  for (const [naam, waarde] of Object.entries(extracted.velden)) {
    const hit = extracted.velden_hits[naam];
    // Velden met source='table' hebben alleen tabel-level bbox (TurboOCR geeft geen
    // per-cel coords) — die zouden exact over de blauwe tabel-outline vallen. Skip
    // de overlay en toon in sidebar met "in tabel"-indicator.
    if (hit?.bbox && hit.source !== "table") {
      out.push({
        id: `veld:${naam}`,
        kind: "veld",
        page: hit.page,
        bbox: hit.bbox,
        label: `${naam} = ${waarde}`,
      });
    }
  }
  for (const [naam, meta] of Object.entries(extracted.tabellen_meta)) {
    if (meta?.bbox) {
      const nRijen = extracted.tabellen[naam]?.length ?? 0;
      out.push({
        id: `tabel:${naam}`,
        kind: "tabel",
        page: meta.page,
        bbox: meta.bbox,
        label: `${naam} (${nRijen} rijen)`,
      });
    }
  }
  for (const [naam, hint] of Object.entries(extracted.mist_hints)) {
    if (hint?.bbox) {
      out.push({
        id: `mist:${naam}`,
        kind: "mist",
        page: hint.page,
        bbox: hint.bbox,
        label: `${naam}: label "${hint.label}" gevonden, geen waarde`,
      });
    }
  }
  return out;
}

function buildOutput(result: UploadResponse): Record<string, unknown> {
  if (result.verdict !== "match" || !result.extracted) {
    return {};
  }
  const out: Record<string, unknown> = {
    document_type: result.matched_type_naam,
    extracted: { ...result.extracted.velden },
  };
  for (const [naam, rows] of Object.entries(result.extracted.tabellen)) {
    (out.extracted as Record<string, unknown>)[naam] = rows;
  }
  if (result.extracted.mist.length > 0) {
    out.warnings = { missing_fields: result.extracted.mist };
  }
  return out;
}
